const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  aacute: "á",
  acirc: "â",
  agrave: "à",
  atilde: "ã",
  auml: "ä",
  eacute: "é",
  ecirc: "ê",
  egrave: "è",
  euml: "ë",
  iacute: "í",
  icirc: "î",
  oacute: "ó",
  ocirc: "ô",
  otilde: "õ",
  uacute: "ú",
  ucirc: "û",
  ccedil: "ç",
  ndash: "–",
  mdash: "—",
  hellip: "…",
};

export function plainText(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&([a-z]+);/gi, (match, name) => NAMED_ENTITIES[name.toLowerCase()] ?? " ")
    .replace(/\s+/g, " ")
    .trim();
}

function walkElements(elements, visit) {
  (Array.isArray(elements) ? elements : []).forEach((element) => {
    if (!element || typeof element !== "object") return;
    visit(element);
    if (Array.isArray(element.columns)) {
      element.columns.forEach((column) => walkElements(column?.elements, visit));
    }
    if (Array.isArray(element.tabs)) {
      element.tabs.forEach((tab) => walkElements(tab?.elements, visit));
    }
  });
}

function pageElements(content) {
  const columns = content?.columns || {};
  return [...(columns.main || []), ...(columns.left || []), ...(columns.right || [])]
    .flatMap((section) => section?.elements || []);
}

export function seoFromContent(content, { storeName = "", fallbackImage = "" } = {}) {
  const settings = content?.settings || {};
  let heading = "";
  let description = "";
  let image = "";
  walkElements(pageElements(content), (element) => {
    if (!heading && element.type === "heading") heading = plainText(element.text);
    if (!description && (element.type === "paragraph" || element.type === "heading")) {
      description = plainText(element.text);
    }
    if (!image && element.type === "image" && /^https:\/\//i.test(element.url || "")) {
      image = element.url;
    }
    if (!image && element.type === "card" && /^https:\/\//i.test(element.imageUrl || "")) {
      image = element.imageUrl;
    }
  });
  const customTitle = plainText(settings.seoTitle);
  const title = customTitle || heading || plainText(storeName) || "Loja";
  return {
    title: title.slice(0, 70),
    customTitle: Boolean(customTitle),
    description: (plainText(settings.seoDescription) || description || plainText(storeName)).slice(0, 160),
    image: /^https:\/\//i.test(settings.seoImage || "") ? settings.seoImage : image || fallbackImage || "",
  };
}

export function publicTitle(seo, storeName) {
  const store = plainText(storeName);
  const title = String(seo?.title || store || "Loja");
  if (seo?.customTitle || !store || title === store) return title.slice(0, 70);
  return `${title} | ${store}`.slice(0, 70);
}

function upsertMeta(attribute, key, content) {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector(selector);
  if (!content) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export function applyPageSeo({ title, description, image, url, index = true }) {
  const safeTitle = String(title || "Loja").slice(0, 70);
  const safeDescription = String(description || "").slice(0, 160);
  document.title = safeTitle;
  upsertMeta("name", "description", safeDescription);
  upsertMeta("property", "og:title", safeTitle);
  upsertMeta("property", "og:description", safeDescription);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", url || "");
  upsertMeta("property", "og:image", image || "");
  upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  upsertMeta("name", "twitter:title", safeTitle);
  upsertMeta("name", "twitter:description", safeDescription);
  upsertMeta("name", "twitter:image", image || "");
  upsertMeta("name", "robots", index ? "index,follow" : "noindex,nofollow");
  if (!url) return;
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = url;
}

export function currentPageUrl() {
  return `${window.location.origin}${window.location.pathname}`;
}
